import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Button, FormControl, Table, Spinner } from "react-bootstrap";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const PageList_VehicleTypeMaster = () => {
  // State management
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rtPage_Add = "/AddVehicleTypeMaster";
  const rtPage_Edit = "/AddVehicleTypeMaster";
  const breadCrumbTitle = "Vehicle Type Master";
  const breadcrumbItem = "Vehicle Type Master";

  const API_URL = `${API_WEB_URLS.MASTER}/0/token/VehicleType`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
      setLoading(false);
    };

    fetchData();
  }, [dispatch, API_URL]);

  const btnAddOnClick = () => {
    navigate(rtPage_Add, { state: { Id: 0 } });
  };

  const btnEditOnClick = (Id) => {
    navigate(rtPage_Edit, { state: { Id } });
  };

  const btnDeleteOnClick = async (Id) => {
    const confirmed = window.confirm("Are you sure you want to delete this vehicle type?");
    if (confirmed) {
      try {
        const result = await Fn_DeleteData(dispatch, Id, API_URL, API_URL + "/Id/0");
        
        // Optional: check if delete was successful
        if (result?.success) {
          // Reload the grid data
          Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
        }
  
      } catch (err) {
        console.error("Delete failed:", err);
      }
    }
  };

  const data = useMemo(() => gridData || [], [gridData]);

  const columns = useMemo(
    () => [
      {
        Header: "Vehicle Category",
        accessor: "VehicleCategory",
        Cell: ({ value }) => (
          <div className="d-flex align-items-center">
            <i className="bx bx-car me-2 text-primary" style={{ fontSize: "1.5rem" }}></i>
            <span className="fw-semibold">{value}</span>
          </div>
        ),
      },
      {
        Header: "Vehicle Type",
        accessor: "Name",
        Cell: ({ value }) => (
          <div className="d-flex align-items-center">
            <i className="bx bx-car me-2 text-primary" style={{ fontSize: "1.5rem" }}></i>
            <span className="fw-semibold">{value}</span>
          </div>
        ),
      },
      {
        Header: "Range From",
        accessor: "RangeFrom",
        Cell: ({ value }) => (
          <span className="text-muted">
            {value ?? "-"}
          </span>
        ),
      },
      {
        Header: "Range To",
        accessor: "RangeTo",
        Cell: ({ value }) => (
          <span className="text-muted">
            {value ?? "-"}
          </span>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="d-flex gap-2 justify-content-center">
            <Button
              className="master-list-edit-btn"
              onClick={() => btnEditOnClick(row.original.Id)}
              size="sm"
            >
              Edit
            </Button>
            <Button
              className="master-list-delete-btn"
              onClick={() => btnDeleteOnClick(row.original.Id)}
              size="sm"
              variant="outline-danger"
            >
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { 
        pageIndex: 0, 
        pageSize: 10,
        sortBy: [
          {
            id: 'Name',
            desc: false
          }
        ]
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div className="master-list-loading d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-muted">Loading vehicle types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-list-container">
      <Container fluid={true} className="px-3 px-md-4">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />

        {/* Controls Section */}
        <div className="master-list-controls master-list-slide-in mb-4">
          <Row className="align-items-center">
            <Col md={8} sm={12} className="mb-3 mb-md-0">
              <div className="search-wrapper">
                <FormControl
                  type="text"
                  placeholder="Search vehicle type..."
                  value={globalFilter || ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="form-control-lg"
                />
              </div>
            </Col>
            <Col md={4} sm={12} className="text-md-end">
              <Button
                type="button"
                onClick={btnAddOnClick}
                className="master-list-add-btn btn-lg px-4"
                variant="primary"
              >
                <i className="fas fa-plus me-2"></i>
                Add New Vehicle Type
              </Button>
            </Col>
          </Row>
        </div>

        {/* Table Section */}
        <div className="master-list-table-container master-list-bounce-in mb-4">
          <div className="table-responsive">
            <Table {...getTableProps()} className="master-list-table table-hover">
              <thead className="custom-header">
                {headerGroups.map((headerGroup, headerGroupIndex) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroupIndex}>
                    {headerGroup.headers.map((column, columnIndex) => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={columnIndex}
                        className="align-middle"
                        style={{ 
                          backgroundColor: '#6D68CB',
                          color: 'white',
                          borderColor: '#6D68CB'
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="fw-semibold">{column.render("Header")}</span>
                          <span className="sort-indicator">
                            {column.isSorted
                              ? column.isSortedDesc
                                ? <i className="fas fa-sort-down" style={{ color: 'white' }}></i>
                                : <i className="fas fa-sort-up" style={{ color: 'white' }}></i>
                              : <i className="fas fa-sort" style={{ color: 'rgba(255,255,255,0.7)' }}></i>}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={rowIndex} className="align-middle">
                      {row.cells.map((cell, cellIndex) => (
                        <td {...cell.getCellProps()} key={cellIndex} className="py-3">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination Section */}
        <div className="master-list-pagination">
          <Row className="align-items-center">
            <Col md={6} sm={12} className="mb-3 mb-md-0">
              <div className="master-list-pagination-info d-flex flex-wrap align-items-center gap-3">
                <span className="text-muted">
                  Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                </span>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Go to:</span>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: '80px' }}
                    defaultValue={pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      gotoPage(page);
                    }}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Show:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: '80px' }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Col>
            <Col md={6} sm={12} className="text-md-end">
              <div className="master-list-pagination-controls d-flex gap-2 justify-content-md-end">
                <Button 
                  className="pagination-btn" 
                  onClick={() => gotoPage(0)} 
                  disabled={!canPreviousPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-double-left"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => previousPage()} 
                  disabled={!canPreviousPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-left"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => nextPage()} 
                  disabled={!canNextPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-right"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => gotoPage(pageCount - 1)} 
                  disabled={!canNextPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-double-right"></i>
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
};

export default PageList_VehicleTypeMaster;

